import React, { useEffect, useMemo, useState } from 'react';
import { Card, Col, DatePicker, Row, Segmented, Space, Statistic, Typography, Alert } from 'antd';
import moment, { Moment } from 'moment';
import { Column, Pie } from '@ant-design/plots';
import { PaymentMethod } from '../types/sales';
import { useResponsive } from '../hooks/useResponsive';
import { apiService } from '../services/apiService';
import { documentsService } from '../services/documentsService';
import './Reports.css';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const CHART_HEIGHT = 300;

const Reports: React.FC = () => {
  const { isMobile } = useResponsive();
  const chartHeight = isMobile ? 240 : CHART_HEIGHT;
  const [range, setRange] = useState<[Moment, Moment]>([
    moment().subtract(6, 'day').startOf('day'),
    moment().endOf('day'),
  ]);
  const [sales, setSales] = useState<any[]>([]);
  const [docs, setDocs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<'7d' | '30d' | '90d'>('7d');

  useEffect(() => {
    const days = period === '7d' ? 6 : period === '30d' ? 29 : 89;
    setRange([moment().subtract(days, 'day').startOf('day'), moment().endOf('day')]);
  }, [period]);

  const rangeStart = range[0].format('YYYY-MM-DD');
  const rangeEnd = range[1].format('YYYY-MM-DD');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch real sales transactions from backend
        const salesData = await apiService.get<any[]>('/pos/transactions', {
          start_date: rangeStart,
          end_date: rangeEnd,
          limit: 1000,
        });
        setSales(Array.isArray(salesData) ? salesData : []);

        // Fetch real documents from backend
        try {
          const docsData = await documentsService.getDocuments();
          setDocs(Array.isArray(docsData) ? docsData : []);
        } catch {
          setDocs([]);
        }
      } catch (err: any) {
        setError('Failed to load sales data. Please try again.');
        setSales([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [rangeStart, rangeEnd]);

  // SALES OVER TIME (daily totals)
  const salesOverTime = useMemo(() => {
    const byDay: Record<string, number> = {};
    const cur = range[0].clone();
    while (cur.isSameOrBefore(range[1], 'day')) {
      byDay[cur.format('YYYY-MM-DD')] = 0;
      cur.add(1, 'day');
    }
    for (const s of sales) {
      const date = s.sale_date || s.created_at?.substring(0, 10);
      if (date) byDay[date] = (byDay[date] || 0) + Number(s.total_amount || 0);
    }
    return Object.entries(byDay).map(([date, value]) => ({ date, value: +value.toFixed(2) }));
  }, [sales, range]);

  // TOP PRODUCTS BY REVENUE
  const topProducts = useMemo(() => {
    const map: Record<string, number> = {};
    for (const s of sales) {
      const items = s.transaction_items || s.items || [];
      for (const it of items) {
        const name = it.product_name || it.name || `Product #${it.product_id}`;
        map[name] = (map[name] || 0) + Number(it.total_price || it.quantity * it.unit_price || 0);
      }
    }
    return Object.entries(map)
      .map(([name, value]) => ({ name, value: +value.toFixed(2) }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [sales]);

  // PAYMENT METHOD SPLIT
  const paymentSplit = useMemo(() => {
    const map: Record<string, number> = {};
    for (const s of sales) {
      const method = s.payment_method || 'OTHER';
      map[method] = (map[method] || 0) + Number(s.total_amount || 0);
    }
    const label: Record<string, string> = {
      CASH: 'Cash', CARD: 'Card', BANK_TRANSFER: 'Bank Transfer',
    };
    return Object.entries(map).map(([k, v]) => ({
      type: label[k] || k,
      value: +v.toFixed(2),
    }));
  }, [sales]);

  // DOCUMENTS BY TYPE
  const docsByType = useMemo(() => {
    const map: Record<string, number> = {};
    for (const d of docs) {
      const t = d.document_type || 'OTHER';
      map[t] = (map[t] || 0) + 1;
    }
    return Object.entries(map).map(([type, count]) => ({ type, value: count }));
  }, [docs]);

  const totalRevenue = sales.reduce((a, s) => a + Number(s.total_amount || 0), 0);
  const avgOrder = sales.length ? totalRevenue / sales.length : 0;

  return (
    <div style={{ minHeight: '100%', overflowY: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <Title level={3} style={{ margin: 0 }}>Reports & Analytics</Title>
        <Space wrap>
          <Segmented
            value={period}
            onChange={(v) => setPeriod(v as any)}
            options={[{ label: '7 Days', value: '7d' }, { label: '30 Days', value: '30d' }, { label: '90 Days', value: '90d' }]}
          />
          <RangePicker value={range as any} onChange={(v) => v && setRange(v as any)} />
        </Space>
      </div>

      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}

      <Row gutter={[16, 16]}>
        <Col xs={24} md={6}>
          <Card loading={loading} className="reports-stat-card page-stat-card page-stat-card--green">
            <Statistic title="Total Revenue" prefix={<Text>Nu. </Text>} value={+totalRevenue.toFixed(2)} />
            <Text type="secondary">Sum of totals in period</Text>
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card loading={loading} className="page-stat-card page-stat-card--blue">
            <Statistic title="Transactions" value={sales.length} />
            <Text type="secondary">Orders in period</Text>
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card loading={loading} className="reports-stat-card page-stat-card page-stat-card--emerald">
            <Statistic title="Average Order" prefix={<Text>Nu. </Text>} value={+avgOrder.toFixed(2)} />
            <Text type="secondary">Revenue / order</Text>
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card loading={loading} className="page-stat-card page-stat-card--violet">
            <Statistic title="Documents" value={docs.length} />
            <Text type="secondary">Files uploaded (all time)</Text>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 12 }}>
        <Col xs={24} lg={14}>
          <Card title="Sales over time" loading={loading}>
            {salesOverTime.length > 0 ? (
              <Column
                height={chartHeight}
                data={salesOverTime}
                xField="date"
                yField="value"
                tooltip={{ formatter: (d: any) => ({ name: 'Revenue', value: `Nu. ${d.value}` }) }}
              />
            ) : (
              <div style={{ height: chartHeight, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
                No sales data for this period
              </div>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card title="Payment methods" loading={loading} className="page-table-card page-table-card--indigo">
            {paymentSplit.length > 0 ? (
              <Pie height={chartHeight} data={paymentSplit} angleField="value" colorField="type" radius={0.9} label={false} />
            ) : (
              <div style={{ height: chartHeight, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
                No payment data
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 12 }}>
        <Col xs={24} lg={12}>
          <Card title="Top products by revenue" loading={loading} className="page-table-card page-table-card--green">
            {topProducts.length > 0 ? (
              <Column
                height={chartHeight}
                data={topProducts}
                xField="name"
                yField="value"
                xAxis={{ label: { autoRotate: true } }}
              />
            ) : (
              <div style={{ height: chartHeight, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
                No product data
              </div>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Documents by type" loading={loading} className="page-table-card page-table-card--violet">
            {docsByType.length > 0 ? (
              <Pie height={chartHeight} data={docsByType} angleField="value" colorField="type" radius={0.9} label={false} />
            ) : (
              <div style={{ height: chartHeight, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
                No documents uploaded
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Reports;
